import './style.scss';

import React, { useEffect, useMemo, useState } from 'react';
import {
  AcquisitionConstantsList,
  KV_MA_COMPATIBILITY_LIMITS,
  getKVLimits,
} from 'app/utils/acquisitionConstants';
import _ from 'lodash';
import { Button, Icon, Input, Popup, Table, TableRow } from 'semantic-ui-react';
import { FormattedMessage, useIntl } from 'react-intl';
import onAcquisitionConstantChange from 'app/components/XRayAreaView/onAcquisitionConstantChange';
import validateAcquisitionConstants from 'app/components/XRayGeneratorAcquisitionConstantsConfiguration/validateAcquisitionConstants';

type EditableAcquisitionConstantsTableProps = {
  acquisitionConstants?: AcquisitionConstantsList;
  onSave?: (acquisitionConstants: AcquisitionConstantsList) => void;
};

type ValidationResult = ReturnType<typeof validateAcquisitionConstants>;

function ErrorIndicator({ error }: { error?: ValidationResult[keyof ValidationResult] }) {
  if (!error) return null;
  const { id, ...rest } = error;
  return (
    <Popup
      trigger={<Icon name="warning sign" color="red" className="xray-generator-error-indicator" />}
      content={<FormattedMessage id={`xray_area.constant.validation_errors.${id}`} {...rest} />}
      position="top center"
      inverted
    />
  );
}

export default function EditableAcquisitionConstantsTable({
  acquisitionConstants = undefined,
  onSave,
}: EditableAcquisitionConstantsTableProps) {
  const intl = useIntl();
  const [rows, setRows] = useState<AcquisitionConstantsList>([]);

  const rowsErrors = useMemo(() => rows.map(validateAcquisitionConstants), [rows]);
  const hasErrors = useMemo(() => rowsErrors.some((errors) => errors !== undefined), [rowsErrors]);

  useEffect(() => {
    setRows(acquisitionConstants.toSorted((a, b) => a.thickness - b.thickness));
  }, [acquisitionConstants]);

  const handleAcquisitionConstantsChange = (
    idx: number,
    field: 'kV' | 'mA' | 's' | 'mAs',
    value: number
  ) => {
    const updated = rows.map((row, i) => {
      if (i !== idx) return row;
      return { ...row, ...onAcquisitionConstantChange(row, field, value) };
    });
    setRows(updated);
  };

  const handleAddRow = () => {
    const last = rows[rows.length - 1];
    const newRow = {
      thickness: last ? last.thickness + 1 : 1,
      kV: last ? last.kV : 50,
      mA: last ? last.mA : 100,
      s: last ? last.s : 0.05,
    };
    const updated = [...rows, newRow];
    setRows(updated);
  };

  const handleRemoveLastRow = () => {
    if (rows.length === 0) return;
    const updated = rows.slice(0, -1);
    setRows(updated);
  };

  const handleSave = () => {
    onSave?.(rows);
  };

  return (
    <div>
      <div className="xray-generator-acquisition-constants-table-container">
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1}>
                <FormattedMessage id="xray_area.constant.thickness" />
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>
                <FormattedMessage id="xray_area.constant.kV" />
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>
                <FormattedMessage id="xray_area.constant.mAs" />
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>
                <FormattedMessage id="xray_area.constant.mA" />
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>
                <FormattedMessage id="xray_area.constant.mS" />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row, idx) => {
              const limits = getKVLimits(row.kV); // Ensure limits are fetched for validation
              const errors = rowsErrors[idx];
              return (
                <Table.Row key={row.thickness} error={errors !== undefined}>
                  <Table.Cell>{row.thickness}</Table.Cell>
                  <Table.Cell>
                    <div className="xray-generator-field">
                      <ErrorIndicator error={errors?.kV} />
                      <Input
                        transparent
                        fluid
                        type="number"
                        value={row.kV}
                        min={40}
                        max={120}
                        onChange={(e) =>
                          handleAcquisitionConstantsChange(idx, 'kV', Number(e.target.value))
                        }
                      />
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      transparent
                      fluid
                      type="number"
                      value={_.round(row.mA * row.s, 1)}
                      step={0.1}
                      onChange={(e) =>
                        handleAcquisitionConstantsChange(idx, 'mAs', Number(e.target.value))
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <div className="xray-generator-field">
                      <ErrorIndicator error={errors?.mA} />
                      <Input
                        transparent
                        fluid
                        type="number"
                        value={row.mA}
                        min={25}
                        max={limits.mA}
                        step={25}
                        onChange={(e) =>
                          handleAcquisitionConstantsChange(idx, 'mA', Number(e.target.value))
                        }
                      />
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="xray-generator-field">
                      <ErrorIndicator error={errors?.s} />
                      <Input
                        transparent
                        fluid
                        type="number"
                        value={Math.round(row.s * 1000)}
                        min={1}
                        step={1}
                        onChange={(e) =>
                          handleAcquisitionConstantsChange(idx, 's', Number(e.target.value) / 1000)
                        }
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
            <TableRow>
              <Table.Cell colSpan={5}>
                <Button.Group>
                  <Button icon positive onClick={handleAddRow}>
                    <Icon name="plus" />
                  </Button>
                  <Button icon negative onClick={handleRemoveLastRow}>
                    <Icon name="minus" />
                  </Button>
                </Button.Group>
              </Table.Cell>
            </TableRow>
          </Table.Body>
        </Table>
      </div>
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <div
          title={
            hasErrors
              ? intl.formatMessage({ id: 'xray_area.constant.validation_errors.invalid' })
              : undefined
          }
        >
          <Button
            primary
            type="button"
            onClick={handleSave}
            disabled={!acquisitionConstants || hasErrors}
          >
            {hasErrors ? <Icon name="warning sign" /> : <Icon name="save" />}
            <FormattedMessage id="general.save" />
          </Button>
        </div>
      </div>
    </div>
  );
}
